import React from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';

import { Maintenance } from '@vyce/core/src/views/maintenance';
import { NoAccess } from '@vyce/core/src/components';
import { GetStarted as TimeGetStarted } from '@vyce/core/src/views/time/components/GetStarted';

import { ROUTES_WITHOUT_NAVIGATION } from 'src/views/main/constants';
import { TeamsSettings } from 'src/modules';

import { Onboarding } from '../views/onboarding/Onboarding';
import { EmployerHiring } from '../views/hiring/EmployerHiring';
import { EmployerCandidates } from '../views/hiring/EmployerCandidates';
import { EmployerPositions } from '../views/positions/EmployerPositions';
import { CompanyPreviewWrapper } from '../views/profile/company/CompanyPreviewWrapper';
import { CompanyProfile } from '../views/profile/company/CompanyProfile';
import { MainDashboard } from '../views/dashboards/MainDashboard';
import { DashboardUserPreview } from '../views/dashboards/employer/DashboardUserPreview';
import { Profile } from '../views/profile/Profile';
import { MainEmailVerification } from '../views/profile/components/MainEmailVerification';
import { Preview } from '../views/profile/Preview';
import { EmployeePaymentsPage, PublicCompanyPreviewWrapper } from '../views/payroll/employee';
import { EmployeeTimeLogs } from '../views/employeeTimeLogs';
import { EmployeePayroll } from '../views/employeePayroll';
import { GeneralAuth } from '../views/auth/general/GeneralAuth';
import { EmployeePreview } from '../views/payroll/components/EmployeePreview';
import { EmployerSubscriptionAndBilling } from '../views/subscriptions/EmployerSubscriptionAndBilling';
import { EmployerUserProfile } from 'src/views/profile/employer/EmployerUserProfile';
import { EmployerPayroll } from '../views/payroll';
import { EmployerPayrollOnboarding } from '../views/payroll/EmployerPayrollOnboarding';
import { EmployerTeams } from '../views/teams';
import { EmployerUserManagement } from '../views/user-management/EmployerUserManagement';
import { UserManagementUserPreview } from '../views/user-management/components';
import { EmployerTimeAndAttendance } from '../views/time';
import { TeamMemberPreview } from '../views/teams/components/TeamMemberPreview';
import { ResetPasswordAndLogin } from '../views/auth/employee/ResetPasswordAndLogin';
import { ManageEmailNotification } from '../views/settings/ManageEmailNotification';
import { EmployerProjects } from '../views/projects/EmployerProjects';
import { OnboardingRoute } from './OnboardingRoute';
import { UnAuthorizedRoute } from './UnAuthorizedRoute';
import { INTERFACE_PERMISSIONS } from '@vyce/core/src/types';

import { EmployerRoute } from './EmployerRoute';
import { PermissionRoute } from './PermissionRoute';
import { AuthorizedRoute } from './AuthorizedRoute';
import { AccountRoute } from './AccountRoute';
import { EmployeeRoute } from './EmployeeRoute';
import { EmployerAITool } from '../views/ai/EmployerAITool';
import { EmployerHelp } from '../views/help/EmployerHelp';
import { WorkerHelp } from '../views/help/WorkerHelp';
import { EmployerMessages } from '../views/messages/EmployerMessages';
import { EmployerForms } from '../views/forms/EmployerForms';

// Module-level route gates. Kept as module constants so PermissionRoute's memo
// key is stable across renders.
const MANAGEMENT_ROUTE_PERMISSIONS = [INTERFACE_PERMISSIONS.STAFF_MANAGEMENT];
const BILLING_ROUTE_PERMISSIONS = [INTERFACE_PERMISSIONS.BILLING];
const CONNECT_ROUTE_PERMISSIONS = [INTERFACE_PERMISSIONS.CONNECT];
const TIME_ROUTE_PERMISSIONS = [INTERFACE_PERMISSIONS.TIME];

// Holding pages for a module the sidebar still shows but this admin can't open.
// Module constants again — PermissionRoute memoises on the fallback's identity.
// Billing has none on purpose: no right, no sidebar item, no page.
const MANAGEMENT_NO_ACCESS = <NoAccess title="You don’t have access to User Management" />;
const CONNECT_NO_ACCESS = <NoAccess title="You don’t have access to Hiring" />;
const TIME_NO_ACCESS = <TimeGetStarted hasPermissions={false} />;

const createRoutes = () => (
  <Switch>
    <Route exact path={'/auth/verify_email/:token'} component={MainEmailVerification} />

    <Route exact path={'/maintenance'} component={Maintenance} />

    {/*Backward-compat redirects for old auth/onboarding URLs*/}
    <Redirect exact from="/auth/employer/signup" to="/auth/signup" />
    <Redirect exact from="/auth/employee/signup" to="/auth/signup" />
    <Redirect exact from="/auth/employer/login" to="/auth/login" />
    <Redirect exact from="/auth/employee/login" to="/auth/login" />
    <Redirect exact from="/auth/employer/verify-phone" to="/auth/verify-phone" />
    <Redirect exact from="/auth/employee/verify-phone" to="/auth/verify-phone" />
    <Route
      exact
      path="/users/login"
      render={({ location }) => <Redirect to={{ pathname: '/auth/login', search: location.search }} />}
    />
    <Route
      exact
      path="/users/register"
      render={({ location }) => <Redirect to={{ pathname: '/auth/signup', search: location.search }} />}
    />

    {/*Authentication*/}
    <UnAuthorizedRoute
      path="/auth/employee/reset-password/:token/:email/:firstName"
      component={ResetPasswordAndLogin}
    />
    <UnAuthorizedRoute path="/auth" component={GeneralAuth} />

    {/*Common*/}
    <OnboardingRoute exact path="/onboarding" component={Onboarding} />
    <AuthorizedRoute path={'/payroll/onboarding'} component={EmployerPayrollOnboarding} />
    <AuthorizedRoute path={'/payroll/onboarding'} component={EmployerPayrollOnboarding} />
    <AuthorizedRoute path={'/vyce-ai'} component={EmployerAITool} />

    <AccountRoute path={ROUTES_WITHOUT_NAVIGATION.EMAIL_NOTIFICATIONS} component={ManageEmailNotification} />
    <AccountRoute path={'/profile/:tab'} component={Profile} />
    <AccountRoute path={'/profile'} component={Preview} />
    <EmployerRoute exact path={'/dashboard/:id'} component={DashboardUserPreview} />
    <AccountRoute path={'/dashboard'} component={MainDashboard} />

    {/*Employer*/}
    <Route
      exact
      path="/hiring/live/:job/candidates"
      render={props => <Redirect to={`/hiring/live/${props.match.params.job}/candidates/matched`} />}
    />
    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      path={'/user-management/:tab/:user/:tab/:companyId'}
      component={PublicCompanyPreviewWrapper}
    />
    <Redirect exact from="/user-management/payroll/:id" to="/user-management/users/:id" />
    <Redirect exact from="/user-management/teams/:id" to="/user-management/users/:id" />
    <Redirect exact from="/user-management/time/:id" to="/user-management/users/:id" />
    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      exact
      path={'/user-management/:tab/:id/:tab'}
      component={EmployerUserProfile}
    />
    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      exact
      path={'/user-management/users/:id'}
      component={UserManagementUserPreview}
    />
    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      exact
      path={'/user-management/admins/:id'}
      component={UserManagementUserPreview}
    />
    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      exact
      path={'/user-management/company-invites/:id'}
      component={TeamMemberPreview}
    />

    <PermissionRoute
      permissions={MANAGEMENT_ROUTE_PERMISSIONS}
      fallback={MANAGEMENT_NO_ACCESS}
      path={'/user-management'}
      component={EmployerUserManagement}
    />
    <EmployerRoute exact path={'/payroll/users/:id/:tab'} component={EmployerUserProfile} />
    <EmployerRoute exact path={'/payroll/users/:id'} component={EmployeePreview} />
    <EmployerRoute path={'/payroll'} component={EmployerPayroll} />
    <PermissionRoute
      permissions={CONNECT_ROUTE_PERMISSIONS}
      fallback={CONNECT_NO_ACCESS}
      path={'/hiring/live/:job/candidates/:category/:candidate'}
      component={EmployerCandidates}
    />
    <PermissionRoute
      permissions={CONNECT_ROUTE_PERMISSIONS}
      fallback={CONNECT_NO_ACCESS}
      path={'/hiring/live/:job/candidates/:category'}
      component={EmployerCandidates}
    />
    <PermissionRoute
      permissions={CONNECT_ROUTE_PERMISSIONS}
      fallback={CONNECT_NO_ACCESS}
      path={'/hiring/live/:job/candidates/'}
      component={EmployerCandidates}
    />
    <PermissionRoute
      permissions={CONNECT_ROUTE_PERMISSIONS}
      fallback={CONNECT_NO_ACCESS}
      path={'/hiring/:status/:job'}
      component={EmployerHiring}
    />
    <PermissionRoute
      permissions={CONNECT_ROUTE_PERMISSIONS}
      fallback={CONNECT_NO_ACCESS}
      path={'/hiring/:status'}
      component={EmployerHiring}
    />
    <EmployerRoute path={'/companies/:id/:tab'} component={CompanyProfile} />
    <EmployerRoute path={'/companies/:id'} component={CompanyPreviewWrapper} />
    <EmployerRoute path={'/companies'} component={EmployerPositions} />

    <PermissionRoute
      permissions={TIME_ROUTE_PERMISSIONS}
      fallback={TIME_NO_ACCESS}
      path={'/time'}
      component={EmployerTimeAndAttendance}
    />

    <EmployerRoute path={'/companies'} component={EmployerPositions} />

    <EmployerRoute exact path={'/teams/list/:team/sync_settings'} component={TeamsSettings} />
    <EmployerRoute exact path={'/teams/list/:team/:id'} component={TeamMemberPreview} />
    <EmployerRoute exact path={'/teams/members/:id'} component={TeamMemberPreview} />
    <EmployerRoute exact path={'/teams/members/:id/:tab'} component={EmployerUserProfile} />
    <EmployerRoute exact path={'/teams/dashboard/:id'} component={TeamMemberPreview} />
    <EmployerRoute exact path={'/teams/dashboard/:id/:tab'} component={EmployerUserProfile} />
    <EmployerRoute path={'/teams'} component={EmployerTeams} />
    <EmployerRoute path={'/projects'} component={EmployerProjects} />

    <PermissionRoute
      permissions={BILLING_ROUTE_PERMISSIONS}
      path={'/subscriptions'}
      component={EmployerSubscriptionAndBilling}
    />
    <EmployerRoute path={'/help'} component={EmployerHelp} />
    <EmployerRoute path={'/messages'} component={EmployerMessages} />

    <EmployerRoute path={'/forms'} component={EmployerForms} />

    <Route path="/hiring" render={() => <Redirect to={{ pathname: '/hiring/live' }} />} />

    {/*Employee*/}
    <EmployeeRoute exact path={'/payments'} component={EmployeePaymentsPage} />
    <EmployeeRoute exact path={'/payments/:id'} component={PublicCompanyPreviewWrapper} />
    <EmployeeRoute path={'/employee-payroll'} component={EmployeePayroll} />
    <EmployeeRoute path={'/timelogs'} component={EmployeeTimeLogs} />
    <EmployeeRoute path={'/help-contact'} component={WorkerHelp} />
    <Route
      path="*"
      render={props => (
        <Redirect to={{ pathname: '/auth/signup', state: { from: props.location.pathname } }} />
      )}
    />
  </Switch>
);

export default createRoutes;
